import { Component, Input, forwardRef, ViewChild, ElementRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatDatepickerModule, MatDatepicker } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatNativeDateModule, DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';

// Custom date formats to ensure dash formatting
export const CUSTOM_DATE_FORMATS = {
  parse: {
    dateInput: 'MM-DD-YYYY',
  },
  display: {
    dateInput: 'MM-DD-YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-calendar',
  imports: [
    CommonModule, 
    FormsModule, 
    MatDatepickerModule, 
    MatInputModule, 
    MatFormFieldModule, 
    MatNativeDateModule
  ],
  templateUrl: './calendar.html',
  styleUrl: './calendar.css',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => Calendar),
      multi: true
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: CUSTOM_DATE_FORMATS
    }
  ]
})
export class Calendar implements ControlValueAccessor {
  @Input() label: string = '';
  @Input() placeholder: string = 'MM-DD-YYYY';
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() errorMessage: string = '';
  @Input() hasError: boolean = false;
  @Input() minDate: Date = new Date(1900, 0, 1); // Default to 1900 for birth dates
  @Input() maxDate: Date = new Date(); // Default to current date for birth dates
  @ViewChild('dateInput') dateInput!: ElementRef<HTMLInputElement>;
  @ViewChild('picker') picker!: MatDatepicker<Date>;

  value: Date | null = null;
  displayValue: string = '';
  hasSelectedDate: boolean = false;
  internalErrorMessage: string = ''; // Internal error message for validation
  isManuallyEditing: boolean = false; // Track when user is manually editing
  
  // Remove the hardcoded minDate and maxDate properties since they're now inputs
  
  private onChange = (value: string) => {};
  private onTouched = () => {};

  constructor(private dateAdapter: DateAdapter<Date>) {
    // Ensure the date adapter formats dates consistently with dashes
    this.dateAdapter.setLocale('en-US');
    
    // Override the format method to ensure consistent dash formatting
    const originalFormat = this.dateAdapter.format.bind(this.dateAdapter);
    this.dateAdapter.format = (date: Date, displayFormat: any) => {
      if (date) {
        return this.formatDateToString(date);
      }
      return originalFormat(date, displayFormat);
    };
  }

  openCalendar(): void {
    if (!this.disabled && this.picker) {
      // If there's a valid manually entered date, ensure it's set as the picker value
      if (this.displayValue && this.displayValue.length === 10) {
        const parsedDate = this.parseStringToDate(this.displayValue);
        if (parsedDate) {
          this.value = parsedDate;
        }
      }
      this.picker.open();
      
      // Ensure proper formatting after calendar closes
      this.picker.closedStream.subscribe(() => {
        setTimeout(() => {
          if (this.value && this.dateInput && this.dateInput.nativeElement) {
            const formattedValue = this.formatDateToString(this.value);
            this.displayValue = formattedValue;
            this.dateInput.nativeElement.value = formattedValue;
          }
        }, 0);
      });
    }
  }

  writeValue(value: string): void {
    if (value) {
      this.value = this.parseStringToDate(value);
      this.displayValue = value;
      this.hasSelectedDate = true;
      // Clear error state if the provided value is valid
      if (this.value) {
        this.hasError = false;
        this.internalErrorMessage = '';
      }
      // Ensure the input shows the dash format
      setTimeout(() => {
        if (this.dateInput && this.dateInput.nativeElement) {
          this.dateInput.nativeElement.value = this.displayValue;
        }
      }, 0);
    } else {
      this.value = null;
      this.displayValue = '';
      this.hasSelectedDate = false;
      this.hasError = false; // Clear error state when no value is provided
      this.internalErrorMessage = ''; // Clear internal error message
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onDateChange(date: Date | null): void {
    // Don't override if user is manually editing
    if (this.isManuallyEditing) {
      return;
    }
    
    this.value = date;
    if (date) {
      this.displayValue = this.formatDateToString(date);
      this.hasSelectedDate = true;
      this.hasError = false; // Clear error state when date is selected from calendar
      this.internalErrorMessage = ''; // Clear internal error message
      this.onChange(this.displayValue);
      
      // Immediately update the input display to ensure dash format
      setTimeout(() => {
        this.updateInputDisplay();
        // Force the input to show our custom formatted value
        if (this.dateInput && this.dateInput.nativeElement) {
          this.dateInput.nativeElement.value = this.displayValue;
        }
      }, 0);
    } else {
      this.displayValue = '';
      this.hasSelectedDate = false;
      this.hasError = false; // Clear error state when date is cleared
      this.internalErrorMessage = ''; // Clear internal error message
      this.onChange('');
    }
    this.onTouched();
  }

  onManualInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    let inputValue = target.value;
    
    // Set flag to indicate manual editing
    this.isManuallyEditing = true;
    
    inputValue = inputValue.replace(/[^0-9\-]/g, '');
    
    inputValue = this.formatMinimally(inputValue);
    
    target.value = inputValue;
    this.displayValue = inputValue;
    
    this.hasSelectedDate = inputValue.length > 0;
    
    if (this.value && inputValue.length < 10) {
      this.value = null;
    }
    
    if (inputValue.length === 10) {
      // Validate the complete date
      const validation = this.validateDateParts(inputValue);
      if (validation.isValid) {
        const parsedDate = this.parseStringToDate(inputValue);
        if (parsedDate) {
          this.value = parsedDate;
          this.hasError = false;
          this.internalErrorMessage = '';
          this.onChange(inputValue);
        } else {
          this.value = null;
          this.hasError = true;
          this.internalErrorMessage = 'Please enter correct date';
          this.onChange(inputValue);
        }
      } else {
        this.value = null;
        this.hasError = true;
        this.internalErrorMessage = validation.errorMessage;
        this.onChange(inputValue);
      }
    } else if (inputValue.length === 0) {
      this.value = null;
      this.hasSelectedDate = false;
      this.hasError = false;
      this.internalErrorMessage = '';
      this.onChange('');
    } else {
      this.value = null;
      this.hasError = false; // Don't show error while typing incomplete date
      this.internalErrorMessage = '';
      this.onChange(inputValue);
    }
    
    // Clear the manual editing flag after a short delay
    setTimeout(() => {
      this.isManuallyEditing = false;
    }, 100);
  }

  onKeyDown(event: KeyboardEvent): void {
    const target = event.target as HTMLInputElement;
    const { value, selectionStart, selectionEnd } = target;
  
    // Always allow navigation and control keys
    const allowedKeyCodes = [9, 27, 13, 46, 37, 38, 39, 40];
    const ctrlAllowed = event.ctrlKey && [65, 67, 86, 88, 90].includes(event.keyCode);
    if (ctrlAllowed || allowedKeyCodes.includes(event.keyCode)) {
      return;
    }
  
    // Only digits, numpad digits and backspace are allowed beyond that
    const isNumber = (!event.shiftKey && event.keyCode >= 48 && event.keyCode <= 57)
                  || (event.keyCode >= 96 && event.keyCode <= 105);
    
    if (event.key === 'Backspace') {
      event.preventDefault();

      // Set flag to indicate manual editing
      this.isManuallyEditing = true;

      // Get the current input value - use displayValue if it exists, otherwise use input value
      let currentValue = this.displayValue || value;
      
      // Force the input to show our current display value if it's different
      if (target.value !== currentValue) {
        target.value = currentValue;
      }
      
      let newValue = currentValue;
      let newCursorPosition = selectionStart ?? 0;
  
      if (selectionStart !== selectionEnd) {
        // If there's a selection, delete the selected text
        newValue = currentValue.slice(0, selectionStart || 0) + currentValue.slice(selectionEnd || 0);
        newCursorPosition = selectionStart || 0;
      } else if (selectionStart && selectionStart > 0) {
        // Delete one character to the left of cursor
        const charToDelete = currentValue[selectionStart - 1];
        
        // If the character to delete is a dash, skip over it and delete the digit before
        if (charToDelete === '-' && selectionStart > 1) {
          newValue = currentValue.slice(0, selectionStart - 2) + currentValue.slice(selectionStart);
          newCursorPosition = selectionStart - 2;
        } else if (charToDelete !== '-') {
          newValue = currentValue.slice(0, selectionStart - 1) + currentValue.slice(selectionStart);
          newCursorPosition = selectionStart - 1;
        } else {
          // If we're at the beginning or only dash to delete, don't do anything
          return;
        }
      } else {
        // At the beginning of input, nothing to delete
        return;
      }
  
      // Extract only digits and reformat
      const digitsOnly = newValue.replace(/[^0-9]/g, '');
      const formatted = this.formatMinimally(digitsOnly);

      // Update the input and component state
      target.value = formatted;
      this.displayValue = formatted;
      this.hasSelectedDate = formatted.length > 0;
      
      // Always reset the parsed date value when manually editing
      this.value = null;
      
      // Check if the formatted date is complete and valid
      if (formatted.length === 10) {
        const validation = this.validateDateParts(formatted);
        if (validation.isValid) {
          const parsedDate = this.parseStringToDate(formatted);
          if (parsedDate) {
            this.value = parsedDate;
            this.hasError = false;
            this.internalErrorMessage = '';
          } else {
            this.hasError = true;
            this.internalErrorMessage = 'Please enter correct date';
          }
        } else {
          this.hasError = true;
          this.internalErrorMessage = validation.errorMessage;
        }
      } else if (formatted.length === 0) {
        this.hasSelectedDate = false;
        this.hasError = false;
        this.internalErrorMessage = '';
      } else {
        // Don't show error while typing incomplete date
        this.hasError = false;
        this.internalErrorMessage = '';
      }
      
      this.onChange(formatted);
      this.onTouched();
  
      // Set cursor position after formatting
      setTimeout(() => {
        // Ensure the input still has our formatted value
        if (target.value !== formatted) {
          target.value = formatted;
        }
        
        // Calculate the appropriate cursor position in the formatted string
        let targetPosition = newCursorPosition;
        
        // Adjust cursor position to account for dashes
        const digitsBeforeCursor = newValue.slice(0, newCursorPosition).replace(/[^0-9]/g, '').length;
        
        // Map digits position to formatted position
        if (digitsBeforeCursor <= 2) {
          targetPosition = digitsBeforeCursor;
        } else if (digitsBeforeCursor <= 4) {
          targetPosition = digitsBeforeCursor + 1; // +1 for first dash
        } else {
          targetPosition = digitsBeforeCursor + 2; // +2 for both dashes
        }
        
        // Ensure position doesn't exceed formatted length
        targetPosition = Math.min(targetPosition, formatted.length);
        
        target.setSelectionRange(targetPosition, targetPosition);
        
        // Clear the manual editing flag after cursor positioning
        this.isManuallyEditing = false;
      }, 0);
  
      return;
    }
  
    // block anything that isn't a number or backspace
    if (!isNumber) {
      event.preventDefault();
    }
  }
  

  private formatMinimally(value: string): string {
    const cleaned = value.replace(/\-/g, '');
    
    let formatted = cleaned;
    
    if (cleaned.length > 2 && cleaned.length <= 4) {
      formatted = cleaned.substring(0, 2) + '-' + cleaned.substring(2);
    } else if (cleaned.length > 4) {
      formatted = cleaned.substring(0, 2) + '-' + cleaned.substring(2, 4) + '-' + cleaned.substring(4, 8);
    }
    
    return formatted;
  }

  private formatAsUserTypesNaturally(value: string): string {
    return this.formatMinimally(value);
  }

  private formatDateToString(date: Date): string {
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}-${day}-${year}`;
  }

  private parseStringToDate(dateString: string): Date | null {
    const parts = dateString.split('-');
    if (parts.length === 3) {
      const month = parseInt(parts[0]) - 1;
      const day = parseInt(parts[1]);
      const year = parseInt(parts[2]);
      
      if (month >= 0 && month <= 11 && day >= 1 && day <= 31 && year >= 1900 && year <= new Date().getFullYear()) {
        const date = new Date(year, month, day);
        if (date.getDate() === day && date.getMonth() === month && date.getFullYear() === year) {
          return date;
        }
      }
    }
    return null;
  }

  private validateDateParts(dateString: string): { isValid: boolean, errorMessage: string } {
    // Only validate if we have a complete date string (10 characters)
    if (dateString.length !== 10) {
      return { isValid: true, errorMessage: '' };
    }

    const parts = dateString.split('-');
    if (parts.length !== 3) {
      return { isValid: false, errorMessage: 'Please enter correct date' };
    }

    const monthStr = parts[0];
    const dayStr = parts[1];
    const yearStr = parts[2];

    // Check if all parts are numbers
    if (!/^\d{2}$/.test(monthStr) || !/^\d{2}$/.test(dayStr) || !/^\d{4}$/.test(yearStr)) {
      return { isValid: false, errorMessage: 'Please enter correct date' };
    }

    const month = parseInt(monthStr);
    const day = parseInt(dayStr);
    const year = parseInt(yearStr);
    const currentYear = new Date().getFullYear();

    // Validate month (1-12)
    if (month < 1 || month > 12) {
      return { isValid: false, errorMessage: 'Please enter correct date' };
    }

    // Validate day (1-31)
    if (day < 1 || day > 31) {
      return { isValid: false, errorMessage: 'Please enter correct date' };
    }

    // Validate year (1900 to current year)
    if (year < 1900 || year > currentYear) {
      return { isValid: false, errorMessage: 'Please enter correct date' };
    }

    // Validate if it's a real date (handles leap years, days per month, etc.)
    const date = new Date(year, month - 1, day);
    if (date.getDate() !== day || date.getMonth() !== (month - 1) || date.getFullYear() !== year) {
      return { isValid: false, errorMessage: 'Please enter correct date' };
    }

    return { isValid: true, errorMessage: '' };
  }

  private updateInputDisplay(): void {
    if (this.dateInput && this.dateInput.nativeElement && this.value) {
      this.dateInput.nativeElement.value = this.formatDateToString(this.value);
    }
  }
}
